import React from "react";
import { CognitoUserSession } from "amazon-cognito-identity-js";
import { AuthEventData, CognitoUserAmplify } from "@aws-amplify/ui";
import { Authenticator } from "@aws-amplify/ui-react";

export interface AuthenticatedUserState {
  isAuthenticated: boolean;
  isUserLoading: boolean;
  authenticatedUser: any;
  currentSession?: CognitoUserSession;
  isHost: boolean;
  isAdmin: boolean;
  signOut: () => void;
}

const initialUserState: AuthenticatedUserState = {
  isAuthenticated: false,
  isUserLoading: true,
  authenticatedUser: undefined,
  currentSession: undefined,
  isHost: false,
  isAdmin: false,
  signOut: () => {},
};

const AuthenticatedUserStateContext =
  React.createContext<AuthenticatedUserState>(initialUserState);

interface InternalProviderProps {
  signOut: ((data?: AuthEventData | undefined) => void) | undefined;
  user?: CognitoUserAmplify;
}

const InternalProvider: React.FC<
  React.PropsWithChildren<InternalProviderProps>
> = ({ signOut, user, children }) => {
  const [userSession, setUserSession] = React.useState<CognitoUserSession>();

  const setUserState = async () => {
    if (user) {
      user.getSession(
        (err: Error | null, session: CognitoUserSession | null) => {
          if (session) {
            setUserSession(session);
          } else {
            throw new Error(`unable to retrieve user session, ${err}`);
          }
        }
      );
    }
  };

  React.useEffect(() => {
    if (!user) {
      console.info("user is undefined");
      return;
    }

    if (!signOut) {
      console.info("signOut is undefined");
      return;
    }

    setUserState();
  }, []);

  const userState = React.useCallback(() => {
    if (!user || !userSession) {
      return initialUserState;
    }

    const token = userSession.getAccessToken();
    if (token) {
      const userGroups = token.payload["cognito:groups"] as Array<string>;
      const foundHostGroup = userGroups && userGroups.includes("Hosts");
      const foundAdminGroup = userGroups && userGroups.includes("Admin");

      return {
        isAuthenticated: true,
        isUserLoading: false,
        authenticatedUser: user,
        currentSession: userSession,
        isHost: foundHostGroup,
        isAdmin: foundAdminGroup,
        signOut: () => {
          if (signOut) {
            signOut();
          }
        },
      };
    } else {
      console.log("no token?");
      return initialUserState;
    }
  }, [userSession, user, signOut]);

  return (
    <AuthenticatedUserStateContext.Provider value={userState()}>
      {children}
    </AuthenticatedUserStateContext.Provider>
  );
};

// Mock provider for local development (bypasses Amplify Authenticator)
const MockAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const mockUserState: AuthenticatedUserState = {
    isAuthenticated: true,
    isUserLoading: false,
    authenticatedUser: {
      username: "local-dev-user",
      attributes: {
        sub: "local-dev-user-id",
        email: "dev@localhost.com",
      },
    },
    currentSession: undefined,
    isHost: false,
    isAdmin: true, // Mock user is always admin
    signOut: () => console.log("Mock sign out"),
  };

  return (
    <AuthenticatedUserStateContext.Provider value={mockUserState}>
      {children}
    </AuthenticatedUserStateContext.Provider>
  );
};

export const AuthenticatedUserProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  // Use mock auth ONLY in local development
  const useMockAuth =
    process.env.REACT_APP_USE_MOCK_AUTH === "true" &&
    process.env.NODE_ENV === "development";

  if (useMockAuth) {
    return <MockAuthProvider>{children}</MockAuthProvider>;
  }

  return (
    <Authenticator>
      {({ signOut, user }) => (
        <InternalProvider signOut={signOut} user={user}>
          {children}
        </InternalProvider>
      )}
    </Authenticator>
  );
};

export const useAuthenticatedUserState = () => {
  const context = React.useContext(AuthenticatedUserStateContext);

  if (!context) {
    throw new Error(
      "useAuthenticatedUserState must be used within AuthenticatedUserStateProvider"
    );
  }

  return context;
};
